import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Brush, ReferenceLine } from 'recharts';
import { ChevronDown, TrendingUp, Loader2, Percent, DollarSign } from 'lucide-react';

const RANGE_OPTIONS = [
  { key: '1W', label: '1W', days: 7 },
  { key: '1M', label: '1M', days: 30 },
  { key: '3M', label: '3M', days: 90 },
  { key: '6M', label: '6M', days: 180 },
  { key: '1Y', label: '1Y', days: 365 },
  { key: 'ALL', label: 'All', days: 9999 },
];

const PLATFORM_COLORS = [
  '#0ea5e9', '#8b5cf6', '#f59e0b', '#10b981', '#ef4444',
  '#ec4899', '#6366f1', '#14b8a6', '#f97316', '#84cc16',
];

const formatValue = (val) => {
  if (val === null || val === undefined || isNaN(val)) return '$0';
  const num = Number(val);
  if (num >= 1000000) return `$${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `$${(num / 1000).toFixed(0)}K`;
  return `$${num.toFixed(0)}`;
};

const formatPct = (val) => `${val >= 0 ? '+' : ''}${val.toFixed(1)}%`;

const CustomTooltip = ({ active, payload, label, pctMode }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 shadow-xl text-xs max-w-xs">
      <p className="text-slate-300 font-medium mb-1">{label}</p>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center justify-between gap-4">
          <span className="flex items-center gap-1.5 truncate">
            <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: p.color }} />
            <span className="text-slate-400 truncate">{p.name}</span>
          </span>
          <span className="text-white font-medium whitespace-nowrap">
            {pctMode ? formatPct(p.value) : formatValue(p.value)}
          </span>
        </div>
      ))}
    </div>
  );
};

const PlatformHistoryChart = ({ userEmail }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [range, setRange] = useState('3M');
  const [expanded, setExpanded] = useState(false);
  const [visiblePlatforms, setVisiblePlatforms] = useState({});
  const [showTotal, setShowTotal] = useState(true);
  const [pctMode, setPctMode] = useState(false);

  const days = RANGE_OPTIONS.find(r => r.key === range)?.days || 90;

  useEffect(() => {
    if (!expanded || !userEmail) return;
    const fetchHistory = async () => {
      setLoading(true);
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/portfolio/history/platforms?user_email=${encodeURIComponent(userEmail)}&days=${days}`
        );
        const json = await res.json();
        if (json.success) setData(json.data || []);
      } catch (err) {
        console.error('Failed to fetch platform history:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, [expanded, userEmail, days]);

  // Extract platform names and build chart data
  const { chartData, pctData, platformNames } = useMemo(() => {
    if (!data.length) return { chartData: [], pctData: [], platformNames: [] };

    const platformSet = new Set();
    data.forEach(d => {
      if (d.byPlatform) Object.keys(d.byPlatform).forEach(k => platformSet.add(k));
    });
    const names = Array.from(platformSet).sort();

    const cd = data.map(d => {
      const row = { date: d.date, Total: d.totalValue || 0 };
      names.forEach(p => { row[p] = d.byPlatform?.[p] || 0; });
      return row;
    });

    // Build percentage data (change from first point)
    const baseTotal = cd[0]?.Total || 1;
    const basePlatforms = {};
    names.forEach(p => { basePlatforms[p] = cd[0]?.[p] || 1; });

    const pd = cd.map(row => {
      const pctRow = { date: row.date };
      pctRow.Total = baseTotal > 0 ? ((row.Total - baseTotal) / baseTotal) * 100 : 0;
      names.forEach(p => {
        const base = basePlatforms[p] || 1;
        pctRow[p] = base > 0 ? ((row[p] - base) / base) * 100 : 0;
      });
      return pctRow;
    });

    return { chartData: cd, pctData: pd, platformNames: names };
  }, [data]);

  // Initialize visibility - platforms OFF by default, user clicks to show
  useEffect(() => {
    if (platformNames.length && !Object.keys(visiblePlatforms).length) {
      const init = {};
      platformNames.forEach(p => { init[p] = false; });
      setVisiblePlatforms(init);
    }
  }, [platformNames]);

  const togglePlatform = useCallback((name) => {
    setVisiblePlatforms(prev => ({ ...prev, [name]: !prev[name] }));
  }, []);

  // Trend for selected range
  const trend = useMemo(() => {
    if (chartData.length < 2) return null;
    const first = chartData[0]?.Total || 0;
    const last = chartData[chartData.length - 1]?.Total || 0;
    const change = last - first;
    const pct = first > 0 ? (change / first) * 100 : 0;
    return { change, pct };
  }, [chartData]);

  const activeData = pctMode ? pctData : chartData;

  return (
    <div className="border-t border-slate-200">
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors"
        data-testid="platform-history-toggle"
      >
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-cyan-600" />
          <span className="text-sm font-semibold text-slate-700">Performance Trend</span>
          {trend && !expanded && (
            <span className={`text-xs font-medium ${trend.change >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
              {trend.change >= 0 ? '+' : ''}{formatValue(Math.abs(trend.change))} ({trend.pct.toFixed(1)}%)
            </span>
          )}
        </div>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>

      {expanded && (
        <div className="px-4 pb-4">
          {/* Controls */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              {/* Range Selector */}
              <div className="flex gap-1">
                {RANGE_OPTIONS.map(opt => (
                  <button
                    key={opt.key}
                    onClick={() => setRange(opt.key)}
                    className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                      range === opt.key
                        ? 'bg-cyan-600 text-white'
                        : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                    }`}
                    data-testid={`range-${opt.key}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              {/* $ / % toggle */}
              <button
                onClick={() => setPctMode(!pctMode)}
                className={`flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                  pctMode ? 'bg-violet-600 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                }`}
                data-testid="pct-mode-toggle"
                title={pctMode ? 'Switch to dollar values' : 'Switch to percentage change'}
              >
                {pctMode ? <Percent className="w-3 h-3" /> : <DollarSign className="w-3 h-3" />}
                {pctMode ? '%' : '$'}
              </button>
            </div>
            {trend && (
              <div className={`text-sm font-semibold ${trend.change >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                {trend.change >= 0 ? '+' : ''}{formatValue(Math.abs(trend.change))}
                <span className="text-xs ml-1">({trend.pct.toFixed(1)}%)</span>
              </div>
            )}
          </div>

          {/* Chart */}
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <Loader2 className="w-6 h-6 text-cyan-600 animate-spin" />
            </div>
          ) : activeData.length === 0 ? (
            <div className="flex items-center justify-center h-48 text-slate-400 text-sm">
              No historical data available for this range
            </div>
          ) : (
            <div className="h-72" data-testid="platform-history-chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={activeData} margin={{ top: 5, right: 5, left: 5, bottom: 25 }}>
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10, fill: '#94a3b8' }}
                    tickFormatter={(d) => {
                      const dt = new Date(d + 'T00:00:00');
                      return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                    }}
                    interval="preserveStartEnd"
                    minTickGap={40}
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: '#94a3b8' }}
                    tickFormatter={pctMode ? (v) => `${v.toFixed(0)}%` : formatValue}
                    width={60}
                  />
                  <Tooltip content={<CustomTooltip pctMode={pctMode} />} />
                  {pctMode && <ReferenceLine y={0} stroke="#94a3b8" strokeDasharray="3 3" />}
                  
                  {showTotal && (
                    <Line type="monotone" dataKey="Total" stroke="#0f172a" strokeWidth={2.5} dot={false} name="Total" />
                  )}
                  {platformNames.map((name, i) =>
                    visiblePlatforms[name] ? (
                      <Line
                        key={name}
                        type="monotone"
                        dataKey={name}
                        stroke={PLATFORM_COLORS[i % PLATFORM_COLORS.length]}
                        strokeWidth={1.5}
                        dot={false}
                        name={name}
                      />
                    ) : null
                  )}
                  
                  {/* Brush for zoom/pan */}
                  {activeData.length > 14 && (
                    <Brush
                      dataKey="date"
                      height={20}
                      stroke="#cbd5e1"
                      fill="#f8fafc"
                      tickFormatter={(d) => {
                        const dt = new Date(d + 'T00:00:00');
                        return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                      }}
                      travellerWidth={8}
                    />
                  )}
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Legend */}
          {activeData.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-slate-100">
              <button
                onClick={() => setShowTotal(!showTotal)}
                className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs transition-colors ${
                  showTotal ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-400'
                }`}
              >
                <span className="w-2.5 h-0.5 rounded" style={{ backgroundColor: showTotal ? '#0f172a' : '#cbd5e1' }} />
                Total
              </button>
              {platformNames.map((name, i) => (
                <button
                  key={name}
                  onClick={() => togglePlatform(name)}
                  className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs transition-colors ${
                    visiblePlatforms[name] ? 'bg-slate-100 text-slate-700' : 'bg-slate-50 text-slate-300'
                  }`}
                >
                  <span
                    className="w-2.5 h-0.5 rounded"
                    style={{ backgroundColor: visiblePlatforms[name] ? PLATFORM_COLORS[i % PLATFORM_COLORS.length] : '#e2e8f0' }}
                  />
                  {name}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PlatformHistoryChart;
